import type { ComputedRef } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { CustomerCreditTransaction, CustomerReferral } from '@entities/customer'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useOnTimezoneChange } from '@entities/viewer'
import { formatPrice, useDayjs } from '@shared/lib/format'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'

import { useCustomerCreditsStore } from './store'
import { CREDITS_HISTORY_PER_PAGE } from '../../api/credits/credits.hook'
import { useCustomerCreditsHistoryApi } from '../../api/credits/credits.hook'
import { useCustomerReferralProgramApi } from '../../api/credits/credits.hook'
import { useCustomerReferralsApi } from '../../api/credits/credits.hook'
import { useCustomerPrivileges } from '../customer-privileges.hook'

const EMPTY = '—'
const DEFAULT_PER_PAGE = 25

interface CustomerCreditsReturn {
  refBalance: ComputedRef<string>
  refReferralLink: ComputedRef<string>
  canManage: ComputedRef<boolean>
  refReferrals: ComputedRef<CustomerReferral[]>
  refReferralsMeta: ComputedRef<Meta>
  isReferralsLoading: ComputedRef<boolean>
  refTransactions: ComputedRef<CustomerCreditTransaction[]>
  isTransactionsLoading: ComputedRef<boolean>
  emptyCell: string
  formatDate: (value: string | null) => string
  formatPrice: (cents: number) => string
  formatAmount: (cents: number) => string
  getCustomerPath: (customerId: number) => string
  openEdit: () => void
  setReferralsPage: (page: number) => void
  setReferralsPerPage: (perPage: number) => void
  reload: () => Promise<void>
}

/**
 * The Credits tab: balance, referral link, the referrals and the credit ledger. The referrals page on their own,
 * so their page state lives here, not in the URL; the ledger is read whole, as the legacy tab showed it.
 */
export const useCustomerCredits = (): CustomerCreditsReturn => {
  const route = useRoute()
  const store = useCustomerCreditsStore()
  const dayjs = useDayjs()
  const { canManageCustomers } = useCustomerPrivileges()

  const customerId = route.params.id as string

  const refBalance = computed<string>(() => formatPrice(store.getProgram?.current_balance ?? 0))
  const refReferralLink = computed<string>(() => store.getProgram?.referral_link ?? '')

  const refReferrals = computed<CustomerReferral[]>(() => store.getReferrals)
  const refReferralsMeta = computed<Meta>(() => store.getReferralsMeta ?? DEFAULT_APP_TABLE_META)
  const isReferralsLoading = computed<boolean>(() => store.isReferralsLoading)

  const refTransactions = computed<CustomerCreditTransaction[]>(() => store.getTransactions)
  const isTransactionsLoading = computed<boolean>(() => store.isTransactionsLoading)

  const formatDate = (value: string | null): string =>
    (value ? dayjs(value).format(DateFormat.KanbanDate) : EMPTY)

  /** `+$25.00` for credits earned or added, `-$10.00` for credits used or removed. */
  const formatAmount = (cents: number): string => (cents > 0 ? `+${formatPrice(cents)}` : formatPrice(cents))

  const getCustomerPath = (id: number): string => resolveUrl(AppRoute.CustomersShow, { id }) ?? ''

  const loadReferrals = (page: number, perPage: number): Promise<CustomerReferral[] | null> =>
    useCustomerReferralsApi({ customerId, page, perPage })

  const loadTransactions = (): Promise<CustomerCreditTransaction[] | null> =>
    useCustomerCreditsHistoryApi({ customerId, page: 1, perPage: CREDITS_HISTORY_PER_PAGE })

  const reload = async (): Promise<void> => {
    await Promise.all([
      useCustomerReferralProgramApi(customerId),
      loadReferrals(refReferralsMeta.value.current_page ?? 1, refReferralsMeta.value.per_page || DEFAULT_PER_PAGE),
      loadTransactions(),
    ])
  }

  const openEdit = (): void => {
    store.setModalEdit(true)
  }

  const setReferralsPage = (page: number): void => {
    void loadReferrals(page, refReferralsMeta.value.per_page || DEFAULT_PER_PAGE)
  }

  const setReferralsPerPage = (perPage: number): void => {
    void loadReferrals(1, perPage)
  }

  onMounted(() => {
    store.reset()
    void reload()
  })

  // Every date arrives pre-shifted into the admin's view timezone.
  useOnTimezoneChange(() => {
    void reload()
  })

  return {
    refBalance,
    refReferralLink,
    canManage: canManageCustomers,
    refReferrals,
    refReferralsMeta,
    isReferralsLoading,
    refTransactions,
    isTransactionsLoading,
    emptyCell: EMPTY,
    formatDate,
    formatPrice,
    formatAmount,
    getCustomerPath,
    openEdit,
    setReferralsPage,
    setReferralsPerPage,
    reload,
  }
}
